
    import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
    import { GetPrintWorld } from '@/common/js/PrintWorld';
    import appconst from '@/lib/appconst';

    @Component({ name: 'PrintWaybillForm' })
    export default class PrintWaybillForm extends Vue {
        // 接收父组件传递的参数
        @Prop({ default: false }) value!: boolean;
        @Prop({ required: true }) rowData!: any;

        // 响应式数据
        boxNumber: string = '';
        selectedPrinter: string = '';
        printerList: any[] = [];
        private printerCacheKey = 'print_printer_last_selection';
        private isLoadPrintList: boolean = false; // 标记打印机是否加载完成

        // 弹框显隐监听
        @Watch('value', { immediate: true })
        onModalVisibleChange(val: boolean) {
            if (val) {
                this.boxNumber = '';
                this.printerList = [];
                this.isLoadPrintList = false;
                this.$nextTick(() => {
                    this.loadPrinters();
                });
            }
        }

        // 加载打印机列表（改用参考代码的回调方式）
        loadPrinters() {
            try {
                const printWorld = new GetPrintWorld(); // 改用参考代码的实例化方式
                printWorld.CallbackOnPrinterList(this.processPrintList.bind(this));

                const json = {
                    action: "printers",
                    refresh: true,
                    defaultprn: true,
                    error: "",
                    val: {}
                };

                if (!printWorld.Act(json)) {
                    const errorMsg = printWorld.GetLastError() || "加载打印机失败";
                    this.$Message.error(errorMsg);
                    return;
                }
            } catch (err) {
                this.$Message.error("加载打印机异常：" + (err as Error).message);
                this.selectedPrinter = '';
            }
        }

        // 处理打印机列表回调（参考代码的processPrintList逻辑）
        processPrintList(json: any) {
            if (json.error && json.error !== '') {
                this.$Message.error("加载打印机失败：" + json.error);
                return;
            }

            // 格式化打印机列表
            this.printerList = Object.values(json.val || {});
            this.isLoadPrintList = true;

            if (this.printerList.length === 0) {
                this.selectedPrinter = '';
                this.$Message.warning("未检测到可用打印机");
                return;
            }

            // 恢复缓存或默认选中第一个
            const lastPrinter = localStorage.getItem(this.printerCacheKey);
            if (lastPrinter && this.printerList.some(item => item.name === lastPrinter)) {
                this.selectedPrinter = lastPrinter;
            } else {
                this.selectedPrinter = this.printerList[0].name;
                this.savePrinterCache(this.selectedPrinter);
            }

            this.$Message.success("已加载打印机，请选择指定打印机");
        }

        // 缓存选择的打印机
        savePrinterCache(printerName: string) {
            if (printerName) {
                localStorage.setItem(this.printerCacheKey, printerName);
            }
        }

        // 执行打印（核心逻辑修改）
        async handlePrint() {
            // 前置校验
            if (!this.isLoadPrintList) {
                this.$Message.warning("打印机列表正在加载，请稍候");
                return;
            }

            if (!this.selectedPrinter) {
                this.$Message.warning("请选择打印机");
                return;
            }

            const rowData = this.rowData;
            const packingDetailList = rowData.packingDetailList || [];
            if (packingDetailList.length === 0) {
                this.$Message.warning("暂无装箱数据，无法打印");
                return;
            }

            // 分场景处理
            if (this.boxNumber.trim()) {
                await this.printSingleBox(this.boxNumber.trim());
            } else {
                await this.printAllUniqueBoxes(packingDetailList);
            }
        }

        // 浏览器预览（保留原有逻辑）
        handlePreview() {
            const rowData = this.rowData;
            const packingDetailList = rowData.packingDetailList || [];
            if (packingDetailList.length === 0) {
                this.$Message.warning("暂无装箱数据，无法预览");
                return;
            }

            if (this.boxNumber.trim()) {
                this.previewSingleBox(this.boxNumber.trim());
            } else {
                this.previewAllUniqueBoxes(packingDetailList);
            }
        }

        // 打印单个箱号（改为Base64方式）
        async printSingleBox(boxNumStr: string) {
            const rowData = this.rowData;
            const targetBox = parseInt(boxNumStr);
            if (isNaN(targetBox)) {
                this.$Message.warning("请输入有效的数字箱号");
                return;
            }

            const filteredList = (rowData.packingDetailList || []).filter((item: any) => item.boxNumber === targetBox);
            if (filteredList.length === 0) {
                this.$Message.warning(`未找到箱号为${targetBox}的装箱数据`);
                return;
            }

            await this.sendPrintRequest(filteredList, rowData, targetBox);
        }

        // 预览单个箱号（保留原有逻辑）
        previewSingleBox(boxNumStr: string) {
            const rowData = this.rowData;
            const targetBox = parseInt(boxNumStr);
            if (isNaN(targetBox)) {
                this.$Message.warning("请输入有效的数字箱号");
                return;
            }

            const filteredList = (rowData.packingDetailList || []).filter((item: any) => item.boxNumber === targetBox);
            if (filteredList.length === 0) {
                this.$Message.warning(`未找到箱号为${targetBox}的装箱数据`);
                return;
            }

            this.openPdfPreview(filteredList, rowData, targetBox);
        }

        // 打印所有唯一箱号（改为Base64方式）
        async printAllUniqueBoxes(packingDetailList: any[]) {
            const boxNumbers = packingDetailList.map((item: any) => item.boxNumber);
            const uniqueBoxNumbers = Array.from(new Set(boxNumbers)).sort((a, b) => a - b);

            if (uniqueBoxNumbers.length === 0) {
                this.$Message.warning("暂无有效箱号数据，无法打印");
                return;
            }

            let successCount = 0;
            let failCount = 0;
            const failBoxes: number[] = [];

            // 逐个打印（异步处理）
            for (const boxNum of uniqueBoxNumbers) {
                const filteredList = packingDetailList.filter((item: any) => item.boxNumber === boxNum);
                try {
                    await this.sendPrintRequest(filteredList, this.rowData, boxNum);
                    successCount++;
                } catch (err) {
                    failCount++;
                    failBoxes.push(boxNum);
                    console.error(`箱号${boxNum}打印失败`, err);
                }
            }

            // 汇总提示
            let tipMsg = `打印完成：成功${successCount}个箱号，失败${failCount}个箱号`;
            if (failBoxes.length > 0) {
                tipMsg += `，失败箱号：${failBoxes.join(',')}`;
            }
            this.$Message.success(tipMsg);
        }

        // 预览所有唯一箱号（保留原有逻辑）
        previewAllUniqueBoxes(packingDetailList: any[]) {
            const boxNumbers = packingDetailList.map((item: any) => item.boxNumber);
            const uniqueBoxNumbers = Array.from(new Set(boxNumbers)).sort((a, b) => a - b);

            if (uniqueBoxNumbers.length === 0) {
                this.$Message.warning("暂无有效箱号数据，无法预览");
                return;
            }

            let previewCount = 0;
            uniqueBoxNumbers.forEach((boxNum) => {
                const filteredList = packingDetailList.filter((item: any) => item.boxNumber === boxNum);
                try {
                    this.openPdfPreview(filteredList, this.rowData, boxNum);
                    previewCount++;
                } catch (err) {
                    this.$Message.error(`箱号${boxNum}预览异常：${(err as Error).message}`);
                }
            });

            this.$Message.success(`已为你打开${previewCount}个箱号的PDF预览窗口`);
        }

        // 发送打印请求（核心修改：PDF转Base64）
        async sendPrintRequest(filteredList: any[], rowData: any, boxNum: number) {
            try {
                // 生成PDF URL
                const packingData = {
                    BoxBatchNumber: rowData.boxBatchNumber || '',
                    ShippingMark: rowData.shippingMark || '',
                    PackingDetailList: filteredList,
                    MaxBox: rowData.maxBox || 0
                };

                const pdfUrl = `${appconst.remoteServiceBaseUrl}api/File/GetPackPdf?infos=${encodeURIComponent(JSON.stringify(packingData))}`;

                // PDF转Base64（参考代码的转换逻辑）
                const base64Data = await this.pdfLinkToBase64(pdfUrl);

                // 构造打印参数（参考代码的格式）
                const printWorld = new GetPrintWorld();
                const printParams = {
                    action: "printfile",
                    format: "file_base64",
                    content: "data:application/pdf;base64," + base64Data,
                    cert: "https://tkyc.ddtmx.com/cer/ddtxianluo.ctf", // 参考代码的证书地址
                    printer: this.selectedPrinter,
                    paperlength: 10, // 参考代码的纸张尺寸
                    paperwidth: 10   // 参考代码的纸张尺寸
                };

                // 执行打印
                const result = printWorld.Act(printParams);
                if (!result || (result.error && result.error !== '')) {
                    const errorMsg = result.error || "打印指令发送失败";
                    throw new Error(errorMsg);
                }

                this.$Message.success(`箱号${boxNum}打印指令已发送`);
            } catch (err) {
                const errorMsg = (err as Error).message || "打印异常";
                this.$Message.error(`箱号${boxNum}打印失败：${errorMsg}`);
                throw err; // 抛出错误供上层统计
            }
        }

        // 打开PDF预览窗口（保留原有逻辑）
        openPdfPreview(filteredList: any[], rowData: any, boxNum: number) {
            try {
                const packingData = {
                    BoxBatchNumber: rowData.boxBatchNumber || '',
                    ShippingMark: rowData.shippingMark || '',
                    PackingDetailList: filteredList,
                    MaxBox: rowData.maxBox || 0
                };

                const pdfUrl = `${appconst.remoteServiceBaseUrl}api/File/GetPackPdf?infos=${encodeURIComponent(JSON.stringify(packingData))}`;
                window.open(pdfUrl, '_blank');

                this.$Message.success(`箱号${boxNum}预览窗口已打开`);
            } catch (err) {
                this.$Message.error(`箱号${boxNum}预览失败：${(err as Error).message}`);
            }
        }

        // PDF转Base64（复用参考代码的转换逻辑）
        async pdfLinkToBase64(url: string) {
            try {
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`获取PDF失败，状态码：${response.status}`);
                }

                const arrayBuffer = await response.arrayBuffer();
                return this.arrayBufferToBase64(arrayBuffer);
            } catch (error) {
                console.error('PDF转Base64失败:', error);
                throw error;
            }
        }

        // ArrayBuffer转Base64（复用参考代码）
        arrayBufferToBase64(buffer: ArrayBuffer) {
            let binary = '';
            const bytes = new Uint8Array(buffer);
            const len = bytes.byteLength;
            for (let i = 0; i < len; i++) {
                binary += String.fromCharCode(bytes[i]);
            }
            return window.btoa(binary);
        }
    }
